/**
 * All static content for the landing page lives here. Kept as TS
 * modules (not JSON) so we get IDE autocomplete + can compose
 * computed strings inline. Swap with a CMS later if we ever want
 * non-engineers editing copy.
 */

import {
  PUBLIC_PLAN_ORDER,
  getPlan,
  annualVariant,
  type PlanDefinition,
  type PlanId,
} from '@pageloop/shared';

/**
 * Canonical hosted-product domain. Single source of truth for every
 * runtime mention of the hosted URL, contact email, and script-tag
 * example. Change here and the whole marketing site updates.
 */
export const HOSTED_DOMAIN = 'pageloop.dev';
export const HOSTED_URL = `https://${HOSTED_DOMAIN}`;
export const HOSTED_CONTACT_EMAIL = `hello@${HOSTED_DOMAIN}`;
/** Top-of-funnel signup URL — Hero CTA + every pricing-card CTA points
 *  here. Pricing CTAs append `?plan=<plan>` so the signup flow can
 *  pre-select the plan after auth. */
export const SIGNUP_URL = `${HOSTED_URL}/signup`;
/** Admin dashboard URL — Nav "Sign in" + "Get started" point here until
 *  the dedicated signup flow at `${SIGNUP_URL}` lands. Same instance
 *  serves both `/dashboard/` and the marketing site at the root. */
export const ADMIN_URL = 'https://pageloop.dev/dashboard';
/** Help / setup documentation. Stubbed at `/docs` for now — the real
 *  guide site lands here later; single source so every "read the docs"
 *  link updates at once. */
export const DOCS_URL = '/docs';
/** Build a plan-pre-selected signup URL. */
export function signupUrlFor(plan?: string): string {
  return plan ? `${SIGNUP_URL}?plan=${encodeURIComponent(plan)}` : SIGNUP_URL;
}

export const NAV_LINKS = [
  { label: 'Product', href: '#features' },
  { label: 'How it works', href: '#how' },
  { label: 'Agents', href: '#agents' },
  { label: 'Pricing', href: '#pricing' },
  { label: 'Docs', href: '/docs' },
  { label: 'GitHub', href: 'https://github.com/rw3iss/pageloop' },
] as const;

export const HERO_BUBBLES: Array<{
  id: string;
  top: string;
  left: string;
  author: string;
  body: string;
}> = [
  {
    id: 'b1',
    top: '24%',
    left: '8%',
    author: 'nora',
    body: 'Could we clarify the rate limit here? 60 req/min feels low for a paid tier.',
  },
  {
    id: 'b2',
    top: '52%',
    left: '14%',
    author: 'jordan',
    body: 'Nice example. Worth adding a Python equivalent below?',
  },
  {
    id: 'b3',
    top: '78%',
    left: '6%',
    author: 'sam',
    body: '✓ resolved — pushed the fix in v0.4.2.',
  },
];

export const LOGOS: Array<{ name: string; mark: string }> = [
  { name: 'Quill',     mark: 'Quill' },
  { name: 'Stagepost', mark: 'stagepost' },
  { name: 'Hyperion',  mark: 'HYPERION' },
  { name: 'Lattice OS',mark: 'lattice/os' },
  { name: 'Notum',     mark: 'NOTUM·' },
  { name: 'Driftbook', mark: 'driftbook' },
];

export const STEPS: Array<{
  n: string;
  title: string;
  body: string;
  code?: string;
  link?: { label: string; href: string };
}> = [
  {
    n: '01',
    title: 'add the script',
    body: 'Inject the UMD script for automatic updates, or use a framework adapter (React, Preact, Solid). No build step required either way.',
    code: `<script src="${HOSTED_URL}/pageloop.umd.js" defer></script>`,
    link: { label: 'How to set up', href: DOCS_URL },
  },
  {
    n: '02',
    title: 'comment on anything',
    body: 'Select text, click a paragraph, or pick an element. Anchors survive edits.',
  },
  {
    n: '03',
    title: 'ship',
    body: 'Reviewers leave feedback in place. Threads resolve. Versions get captured.',
  },
];

export const FEATURES: Array<{
  kicker: string;
  title: string;
  body: string;
  highlights: string[];
}> = [
  {
    kicker: 'anchoring',
    title: 'pin a comment to anything that renders.',
    body: 'Comments stick to the right place even when the page changes. Multi-strategy anchors fall back gracefully — misaligned threads are flagged for review, never deleted.',
    highlights: [
      'Text, paragraph, or element-picker selection.',
      'Fuzzy re-anchor when content edits.',
      'Misalignment flagged, never lost.',
    ],
  },
  {
    kicker: 'real-time',
    title: 'live, with no extra infrastructure.',
    body: 'WebSocket first, with SSE and REST fallbacks. Comments and edits land instantly — and built-in live chat lets team members collaborate and talk through changes in real time. No third-party realtime service to bolt on.',
    highlights: [
      'WS / SSE / REST autoselect.',
      'Live team chat for real-time collaboration.',
      'Per-page presence and avatars.',
      'No external broker required.',
    ],
  },
  {
    kicker: 'change-tracking',
    title: 'every comment knows its version.',
    body: 'Connect a git repo and PageLoop captures a snapshot on every commit. When pages change, threads re-anchor automatically or surface for review.',
    highlights: [
      'Git-tracked versions per page.',
      'Inline diff next to the discussion.',
      'Diff-aware re-anchoring on edit.',
    ],
  },
  {
    kicker: 'integrations',
    title: 'hosted, zero-ops.',
    body: `Add one script tag — or wire your app to the widget — and you're live on ${HOSTED_DOMAIN}. No servers to stand up, no database to run, no ops. We host it; you ship.`,
    highlights: [
      'One script tag, or a framework adapter.',
      'No server or database to run.',
      `Managed end-to-end at ${HOSTED_DOMAIN}.`,
      'Free tier to start.',
    ],
  },
];

export const TICKER_COMMENTS: Array<{ author: string; project: string; body: string; ago: string }> = [
  { author: 'nora',    project: 'acme-docs',     body: 'Should this say "max 60 req/min" or "60 req per minute"?',   ago: '12s' },
  { author: 'jordan',  project: 'stagepost',     body: 'Loving the new error formatter. One nit — the icon is hard to read on dark.', ago: '38s' },
  { author: 'sam',     project: 'hyperion',      body: '+1 to the rate-limit change. Trying it now.',               ago: '1m' },
  { author: 'priya',   project: 'lattice/os',    body: 'Annotated the install step — there\'s a missing sudo.',     ago: '2m' },
  { author: 'alex',    project: 'driftbook',     body: 'Comment on the auth diagram: do we still need step 3?',     ago: '4m' },
  { author: 'rin',     project: 'notum',         body: 'Resolved — fixed in 0.4.2.',                                ago: '6m' },
  { author: 'kai',     project: 'quill',         body: 'Great walkthrough. Could we add a curl example for /v1/items?', ago: '8m' },
  { author: 'noor',    project: 'acme-docs',     body: 'The screenshot here is stale — the toolbar moved last week.', ago: '11m' },
];

/**
 * Pricing card content is *derived* from @pageloop/shared `PLAN_CATALOG`
 * — id, display name, and price come straight from the catalog so the
 * marketing cards can never drift out of sync with what Stripe actually
 * charges or with the plan ids the signup flow accepts. Only the
 * marketing-specific tagline + feature bullets live here, as a per-id
 * copy override keyed by `PlanId`.
 *
 * The rendered card list is built by mapping `PUBLIC_PLAN_ORDER` →
 * catalog data + the copy override below. Pricing CTAs route to the
 * SaaS signup with `?plan=<id>` so the post-auth wizard can pre-select;
 * Enterprise routes to a mailto. Every emitted `?plan=` value is a real
 * `PlanId`, so `getPlan()` server-side can never reject it.
 *
 * To change a price or a plan's caps, edit the catalog
 * (`packages/shared/types/src/plans.ts`) — nothing here needs touching.
 * To reword a tagline or bullet list, edit `PLAN_COPY` below.
 */

/** Marketing-only copy per plan id: friendlier tagline + the card's
 *  feature bullets. The catalog owns id/name/price; this owns prose. */
interface PlanCopy {
  /** Overrides the catalog tagline when set; falls back to it otherwise. */
  tagline?: string;
  features: string[];
}

const PLAN_COPY: Partial<Record<PlanId, PlanCopy>> = {
  free: {
    tagline: 'Free forever.',
    features: [
      '1 project + 5 members',
      'Unlimited anonymous comments',
      'Public projects + community',
    ],
  },
  indie: {
    features: [
      'Up to 5 projects',
      'Up to 10 members',
      'Custom branding',
      'Private projects',
      'Email support',
    ],
  },
  pro: {
    features: [
      'Up to 10 projects',
      'Up to 15 members',
      'Custom branding · private projects',
      'Email digests',
      '14-day free trial',
      'Email support',
    ],
  },
  team: {
    features: [
      'Unlimited projects',
      'Unlimited members',
      'SSO (SAML, Google Workspace)',
      'Audit log',
      'Priority Slack support',
    ],
  },
  enterprise: {
    features: [
      'Everything in Team',
      '99.9% SLA',
      'Dedicated CSM',
      'Dedicated capacity + region options',
      'DPA / SOC 2 documentation',
      'Custom contract & invoicing',
    ],
  },
};

export interface PricingCard {
  /** Real wire-level plan id — safe to feed into `?plan=`. */
  tier: PlanId;
  name: string;
  /** Headline amount, e.g. `$0`, `$10`, or `Custom`. */
  price: string;
  /** Period suffix, e.g. `/ month`, `forever`, or `''`. */
  period: string;
  /** Smaller print under the price; the annual deal when one exists. */
  annualNote?: string;
  tagline: string;
  features: string[];
  cta: string;
  ctaHref: string;
  popular?: boolean;
}

/** Split a catalog `displayPrice` into the card's amount + period.
 *  `'$10 / month'` → `{ amount: '$10', period: '/ month' }`;
 *  `'$0'` → `{ amount: '$0', period: 'forever' }`; `null` → Custom. */
function splitPrice(plan: PlanDefinition): { amount: string; period: string } {
  if (plan.displayPrice === null) return { amount: 'Custom', period: '' };
  const [amount, ...rest] = plan.displayPrice.split(' / ');
  const cadence = rest.join(' / ');
  if (!cadence) {
    // No cadence segment — `$0`. Free reads better as "forever".
    return { amount: amount ?? plan.displayPrice, period: 'forever' };
  }
  return { amount: amount ?? plan.displayPrice, period: `/ ${cadence}` };
}

/** Parse a numeric dollar amount out of a `$NN` / `$NN / month` string;
 *  null when there's no figure (e.g. Custom). */
function dollars(displayPrice: string | null): number | null {
  if (!displayPrice) return null;
  const match = displayPrice.match(/\$(\d+)/);
  return match ? Number(match[1]) : null;
}

/** Build the "or $X/year — save $Y" note from the monthly + annual
 *  catalog twins. Returns undefined when the plan has no annual variant
 *  or either price is non-numeric. */
function annualNoteFor(plan: PlanDefinition): string | undefined {
  const annualId = annualVariant(plan.id);
  if (!annualId) return undefined;
  const monthly = dollars(plan.displayPrice);
  const annual = dollars(getPlan(annualId).displayPrice);
  if (monthly === null || annual === null) return undefined;
  const saving = monthly * 12 - annual;
  return `or $${annual}/year — save $${saving}`;
}

function ctaHrefFor(plan: PlanDefinition): string {
  if (plan.cadence === 'contact') {
    return `mailto:${HOSTED_CONTACT_EMAIL}?subject=Enterprise%20inquiry`;
  }
  return signupUrlFor(plan.id);
}

export const PRICING: PricingCard[] = PUBLIC_PLAN_ORDER.map((id) => {
  const plan = getPlan(id);
  const copy = PLAN_COPY[id];
  const { amount, period } = splitPrice(plan);
  return {
    tier: plan.id,
    name: plan.name,
    price: amount,
    period,
    annualNote: annualNoteFor(plan),
    tagline: copy?.tagline ?? plan.tagline,
    features: copy?.features ?? [],
    cta: plan.ctaLabel,
    ctaHref: ctaHrefFor(plan),
    popular: plan.popular,
  };
});

export const FAQ: Array<{ q: string; a: string }> = [
  {
    q: 'Do I need to host a server?',
    a: `No. PageLoop is fully managed on ${HOSTED_DOMAIN} — there's nothing to install, run, or maintain. Create a project, drop in the loader snippet (or wire your app to the widget), and commenting + change-tracking work immediately. The free tier is hosted end-to-end.`,
  },
  {
    q: 'How does the comment anchor survive page edits?',
    a: 'Each anchor stores a multi-strategy fingerprint: a W3C text fragment, a scoped CSS path, an XPath fallback, and fuzzy prefix/suffix context. When the page changes, the anchor engine tries each strategy in order. Comments that fail every strategy are flagged misaligned, not deleted — the author re-anchors them.',
  },
  {
    q: 'What about my static-site framework?',
    a: 'If it produces HTML at build or request time, PageLoop works. We\'ve tested against Astro, Starlight, Mintlify, Docusaurus, Next.js, Hugo, Jekyll, and plain hand-written HTML. SPA frameworks use a single npm import; static sites get a `<script>` tag.',
  },
  {
    q: 'Is PageLoop only for documentation?',
    a: 'No — it works on any rendered HTML. Teams use it for design reviews on internal admin UIs, feedback on long-form blog drafts, annotating prototypes, even commenting on running app surfaces during QA.',
  },
  {
    q: 'How do I authenticate users?',
    a: 'GitHub and Google OAuth are wired today. SSO (SAML + Google Workspace) is gated to the Pro tier. For evaluation, the dev-token shim auto-issues a JWT to a configured email so you can try PageLoop without configuring OAuth at all.',
  },
  {
    q: 'What happens to my data if I cancel?',
    a: `Your data exports as JSON via the admin dashboard at any time. Cancelled orgs get a 30-day soft-delete window before any data is purged.`,
  },
  {
    q: 'Do I need a credit card to try a paid tier?',
    a: 'No. The Free tier is the trial — generous enough (1 project, up to 5 members, unlimited commenters) that you can validate the value end-to-end. Indie, Pro, and Team get 14 days of full feature preview; Stripe Checkout asks for a card at the end of those 14 days, not before.',
  },
  {
    q: 'Monthly or annual billing?',
    a: 'Both. Annual saves about 17% (two months free) — $50/year for Indie, $100/year for Pro, $200/year for Team. You can switch between monthly and annual from the billing page; downgrades wait until the current period ends so you keep what you paid for.',
  },
];

export const FOOTER_LINKS: Array<{ heading: string; links: Array<{ label: string; href: string }> }> = [
  {
    heading: 'Product',
    links: [
      { label: 'Features',    href: '#features' },
      { label: 'Pricing',     href: '#pricing' },
      { label: 'Changelog',   href: '#' },
      { label: 'Roadmap',     href: '#' },
    ],
  },
  {
    heading: 'Developers',
    links: [
      { label: 'Docs',        href: '/docs' },
      { label: 'GitHub',      href: 'https://github.com/rw3iss/pageloop' },
      { label: 'npm',         href: 'https://www.npmjs.com/package/@pageloop/cli' },
      { label: 'Library reference', href: '/docs/libraries' },
    ],
  },
  {
    heading: 'Company',
    links: [
      { label: 'About',       href: '#' },
      { label: 'Blog',        href: '#' },
      { label: 'Contact',     href: `mailto:${HOSTED_CONTACT_EMAIL}` },
      { label: 'Status',      href: '/status' },
    ],
  },
  {
    heading: 'Legal',
    links: [
      { label: 'Privacy',     href: '#' },
      { label: 'Terms',       href: '#' },
      { label: 'DPA',         href: 'https://github.com/rw3iss/pageloop/blob/main/docs/legal/DPA.md' },
      { label: 'Security',    href: '#' },
    ],
  },
];
